import { unwrapResult } from "@reduxjs/toolkit";
import { captureException } from "@sentry/browser";
import React, { type JSX, useEffect } from "react";
import { t } from "ttag";

import { IProduct } from "../../../models/catalogue";
import {
    IImageURL,
    IProductID,
    IProductSKU,
    ISafeHTML,
    WebPageURL,
} from "../../../models/nominals";
import { IAPIPrice } from "../../../models/prices";
import { VariantDescriptionBlock } from "../../../models/streamfield-blocks";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import {
    trackConfiguratorLoadedEvent,
    trackListrackProductBrowse,
    trackProductValuePageView,
    trackVariantSelectedEvent,
    trackViewItemEvent,
} from "../../../utils/analytics";
import { PriceType, getProductPrice } from "../../../utils/catalogue";
import { decimal } from "../../../utils/format";
import { isZipLocation } from "../../../utils/guards";
import { ZERO } from "../../../utils/money";
import { showFinanceBlock } from "../ConfiguratorPanelTrigger";
import { defaults } from "../defaults";
import {
    getSelectedUpgradeIDFromURL,
    pushPDPVariantURLChange,
    startHistoryListener,
} from "../history";
import {
    IModularConfiguratorOptionSet,
    IUpsellModalComponentFn,
} from "../models";
import {
    setAddToBasketBtnState,
    setSelectedRootProduct,
    setSelectedUpgrade,
} from "../reducers";
import {
    addonVariantPricesSelector,
    baseVariantSelector,
    rootProductSelector,
    upgradedVariantPriceSelector,
    upgradedVariantSelector,
} from "../selectors";
import {
    loadConcreteBundles,
    loadFinancingPlans,
    loadLocation,
    loadPrice,
    loadProducts,
    loadShippingMethod,
} from "../thunks";
import { isPreorderProduct } from "../utils";
import { Configurator as Component } from "./ConfiguratorContent";

export interface IProps {
    optionSet: IModularConfiguratorOptionSet;

    // CMS Page Data
    title?: string;
    preheader?: string;
    subhead?: string;
    description?: string;
    descriptions?: readonly VariantDescriptionBlock[];
    promoCopy?: string;
    liveChatHeader?: string;
    chatLinkComponent?: JSX.Element | null;

    showImage?: boolean;
    showShippingLead?: boolean;

    // Site Page URLs for links and redirects
    financingLink: WebPageURL;
    basketLink: WebPageURL;
    configureGiftsLink: WebPageURL;

    // Optional: Up Sell Modal functionality
    showUpsellModal?: (product: IProduct | null) => boolean;
    showUpsellModalIcon?: boolean | false;
    getUpsellModalComponentFn?: (
        product: IProduct,
    ) => IUpsellModalComponentFn | null;

    // Colors
    buttonColor?: string;

    // Behavior options
    enableHistoryInteraction?: boolean;

    // Misc UI options
    buildAdditionalContent?: (
        rootProduct: IProduct | null,
        optionSet: IModularConfiguratorOptionSet,
    ) => JSX.Element | null;
    configType?: string;
    getDeliveryCopy?: (
        rootProduct: IProduct,
        isPreorder: boolean,
    ) => string | null;
    getDeliveryIcon?: () => IImageURL | null;
    showFeelScale?: boolean;
    showPreTitle?: boolean;

    learnMoreSelector?: string;
    hideLearnMoreLink?: boolean;

    showVariantCallout?: boolean;

    starRatingURL?: WebPageURL;
    strikeThroughMSRP?: boolean;
    actualPriceStyle?: string;
    boxHeaderContent?: string;
    topBadgeContent?: ISafeHTML | string;
    overrideFinancingCopy?: string;
    supDollarSign?: boolean;

    showStarRatingsModal?: boolean;
    showFinancingModal?: boolean;
    phoneNumber?: string;
    showImagePerUpgradeOption?: boolean;

    isPlpPanelStyle?: boolean;
    useCompareDrawerCenter?: boolean;
}

// Helper functions extracted from component
const getRootProductID = (
    rootProduct: IProduct | null,
    optionSet: IModularConfiguratorOptionSet,
): IProductID => {
    return rootProduct ? rootProduct.id : optionSet.product_ids[0];
};

const getCurrentProductID = (
    rootProduct: IProduct | null,
    optionSet: IModularConfiguratorOptionSet,
): IProductID => {
    return rootProduct ? rootProduct.id : optionSet.product_ids[0];
};

const getDescription = (
    descriptions: readonly VariantDescriptionBlock[] | undefined,
    description: string | undefined,
    productID: IProductID,
): string | undefined => {
    if (descriptions && descriptions.length) {
        const match = descriptions.find(({ product }) => {
            return product === productID;
        });
        if (match && match.description) {
            return match.description;
        }
    }
    return description;
};

const trackPageView = (
    upgradedVariant: IProduct | null,
    rootProducts: readonly IProduct[],
    price: IAPIPrice | null,
    baseVariant: IProduct | null,
): void => {
    const skus: ReadonlyArray<IProductSKU> = upgradedVariant
        ? upgradedVariant.skus
        : rootProducts.reduce<ReadonlyArray<IProductSKU>>(
              (skuList, product) => skuList.concat(product.skus),
              [],
          );
    const priceValue = decimal(
        price
            ? getProductPrice(price, {
                  priceType: PriceType.COSMETIC_EXCL_TAX,
                  includePostDiscountAddons: true,
                  quantity: 1,
              })
            : ZERO,
    );
    trackProductValuePageView(skus, priceValue, "product", "ProductPageView");
    if (baseVariant) {
        trackViewItemEvent(baseVariant);
    }
};

const buildAdditionalContentHelper = (
    buildAdditionalContent:
        | ((
              rootProduct: IProduct | null,
              optionSet: IModularConfiguratorOptionSet,
          ) => JSX.Element | null)
        | undefined,
    rootProduct: IProduct | null,
    optionSet: IModularConfiguratorOptionSet,
): JSX.Element | null => {
    if (!buildAdditionalContent || !rootProduct) {
        return null;
    }
    return buildAdditionalContent(rootProduct, optionSet);
};

export const Configurator = (props: IProps): React.ReactNode => {
    const {
        optionSet,
        title,
        preheader,
        subhead,
        description: descriptionProp,
        descriptions,
        promoCopy,
        liveChatHeader,
        chatLinkComponent,
        showImage,
        showShippingLead,
        financingLink,
        basketLink,
        configureGiftsLink,
        showUpsellModal,
        showUpsellModalIcon,
        getUpsellModalComponentFn,
        buttonColor = "primary-congress-blue",
        enableHistoryInteraction,
        buildAdditionalContent,
        configType = "pdp-hero",
        getDeliveryCopy = () => null,
        showFeelScale = false,
        showPreTitle = false,
        learnMoreSelector = "#pdp-features",
        hideLearnMoreLink,
        showVariantCallout = false,
        starRatingURL,
        strikeThroughMSRP = false,
        actualPriceStyle = "",
        boxHeaderContent = "",
        topBadgeContent = "",
        overrideFinancingCopy = "",
        supDollarSign,
        showStarRatingsModal,
        showFinancingModal,
        phoneNumber,
        showImagePerUpgradeOption = false,
        isPlpPanelStyle,
        useCompareDrawerCenter = false,
    } = props;

    // Redux selectors
    const state = useAppSelector(
        (rootState) => rootState.configurator || defaults,
    );
    const rootProducts = state.entities.rootProducts;
    const concreteBundles = state.entities.concreteBundles;
    const rootProduct = useAppSelector((rootState) =>
        rootProductSelector(rootState.configurator || defaults),
    );
    const baseVariant = useAppSelector((rootState) =>
        baseVariantSelector(rootState.configurator || defaults),
    );
    const selectedUpgradeID = state.ui.selectedUpgrade;
    const upgradedVariant = useAppSelector((rootState) =>
        upgradedVariantSelector(rootState.configurator || defaults),
    );
    const price = useAppSelector((rootState) =>
        upgradedVariantPriceSelector(rootState.configurator || defaults),
    );
    const financingPlans = state.entities.financingPlans;
    const shippingMethod = state.shippingMethod;
    const quantity = state.ui.quantity;
    const selectedAddonPrice = useAppSelector((rootState) =>
        addonVariantPricesSelector(rootState.configurator || defaults),
    );
    const addToBasketButtonText = state.ui.addToBasketButtonText;
    const optionSelectionErrorReason = state.ui.optionSelectionErrorReason;

    const postCode = useAppSelector((rootState) => {
        return rootState.common.enteredLocation &&
            isZipLocation(rootState.common.enteredLocation)
            ? rootState.common.enteredLocation.zip
            : rootState.common.detectedLocation &&
                rootState.common.detectedLocation.zip
              ? rootState.common.detectedLocation.zip
              : "";
    });

    // Dispatchers
    const dispatch = useAppDispatch();

    // Memoized values
    const currentProductID = getCurrentProductID(rootProduct, optionSet);
    const description = getDescription(
        descriptions,
        descriptionProp,
        currentProductID,
    );

    // Load shipping methods function
    const loadShippingMethods = async (): Promise<void> => {
        const rootProductID = getRootProductID(rootProduct, optionSet);
        let zipCode = postCode;
        if (!zipCode) {
            const result = unwrapResult(await dispatch(loadLocation()));
            zipCode = result?.zip ?? "";
        }
        dispatch(
            loadShippingMethod({ productID: rootProductID, postCode: zipCode }),
        );
    };

    // Update add to basket text function
    const updateAddToBasketText = (): void => {
        const isPreorder =
            upgradedVariant && isPreorderProduct(upgradedVariant);
        const newAddToBasketButtonText = isPreorder
            ? t`Pre-Order`
            : t`Add to Cart`;
        if (addToBasketButtonText !== newAddToBasketButtonText) {
            dispatch(
                setAddToBasketBtnState({
                    cooldownActive: false,
                    text: newAddToBasketButtonText,
                }),
            );
        }
    };

    // Mount effect - intentionally empty deps to run once on mount only.
    // Values like rootProducts, concreteBundles, shippingMethod, financingPlans are read from
    // SSR-preloaded Redux state and should be stable on initial mount. Dynamic updates after
    // mount (e.g., variant changes) are handled by the update effect below.
    useEffect(() => {
        if (enableHistoryInteraction) {
            startHistoryListener(dispatch);
        }

        // Load main product from API (skip if already pre-populated from SSR).
        // Under full-document hydrateRoot(document) there is no per-island error
        // boundary, so throwing here would blank the whole page; on an empty
        // product set we report and skip the load instead.
        if (rootProducts.length === 0) {
            if (optionSet.product_ids.length <= 0) {
                captureException(
                    new Error(
                        "Configurator optionSet.product_ids is empty; skipping product load",
                    ),
                    {
                        extra: {
                            productIds: optionSet.product_ids,
                            initialVariantID: optionSet.initial_variant_id,
                        },
                    },
                );
            } else {
                dispatch(
                    loadProducts({
                        productIDs: optionSet.product_ids,
                        initialVariantID: optionSet.initial_variant_id,
                    }),
                );
            }
        }

        // Load Bundles (skip if already pre-populated from SSR)
        const baseVariantID = baseVariant?.id || null;
        if (
            baseVariantID &&
            (!concreteBundles || concreteBundles.length === 0)
        ) {
            const urlSelectedUpgradeID = getSelectedUpgradeIDFromURL();
            dispatch(
                loadConcreteBundles({
                    variantID: baseVariantID,
                    selectedUpgradeID: urlSelectedUpgradeID,
                }),
            );
        } else if (baseVariantID) {
            // The CDN strips the `upgrade` param before it reaches Django, so
            // SSR always prefills the default. Apply the URL selection on
            // hydration (the prefilled bundles already contain the upgrades, so
            // we only set the selected ID — no redundant bundle reload).
            const urlSelectedUpgradeID = getSelectedUpgradeIDFromURL();
            if (
                urlSelectedUpgradeID &&
                urlSelectedUpgradeID !== selectedUpgradeID
            ) {
                dispatch(setSelectedUpgrade(urlSelectedUpgradeID));
            }
        }

        trackConfiguratorLoadedEvent();

        // Load shipping method (skip if already pre-populated from SSR)
        // Always call loadLocation() to potentially refine location from client-side detection
        // If location differs from SSR, the postCode change handler will refetch shipping
        if (shippingMethod) {
            dispatch(loadLocation());
        } else {
            loadShippingMethods();
        }

        // Load financing details to display product-specific plans if available
        // (skip if already pre-populated from SSR)
        if (!financingPlans || financingPlans.length === 0) {
            const productID =
                baseVariant?.id ||
                optionSet.initial_variant_id ||
                currentProductID;
            dispatch(loadFinancingPlans({ productID }));
        }
    }, []);

    // Reviews products are now loaded via SSR prefill in the configurator's
    // useMemo (see configurator/ssr.ts). The old useEffect thunk dispatch
    // was removed because it raced with ProductReviews' SSR prefill,
    // causing stale facets in the reviews filter.

    // componentDidUpdate effects - track previous values
    const prevBaseVariantID = React.useRef<IProductID | null>(null);
    const prevUpgradedVariantID = React.useRef<IProductID | null>(null);
    const prevQuantity = React.useRef<number>(quantity);
    const prevPostCode = React.useRef<string>(postCode);
    const prevPrice = React.useRef<IAPIPrice | null>(null);
    const prevSelectedUpgradeID = React.useRef<IProductID | null>(
        selectedUpgradeID,
    );
    const hasCompletedFirstRender = React.useRef(false);

    useEffect(() => {
        if (hasCompletedFirstRender.current) {
            showFinanceBlock();
        } else {
            hasCompletedFirstRender.current = true;
        }

        const nextBaseVariantID = baseVariant?.id || null;
        const nextUpgradedVariantID = upgradedVariant?.id || null;
        const baseVariantChanged =
            prevBaseVariantID.current !== nextBaseVariantID;
        const upgradedVariantChanged =
            prevUpgradedVariantID.current !== nextUpgradedVariantID;
        const qtyChanged = prevQuantity.current !== quantity;
        const selectedUpgradeInConfigurator =
            selectedUpgradeID &&
            rootProducts
                .map((product: IProduct) => product.id)
                .includes(selectedUpgradeID);
        const selectedUpgradeChanged =
            selectedUpgradeID &&
            selectedUpgradeID !== prevSelectedUpgradeID.current;

        // If the base variant changed, reload the bundle and financing data for the new variant
        if (baseVariantChanged && nextBaseVariantID) {
            const selectedUpgradeID = getSelectedUpgradeIDFromURL();
            dispatch(
                loadConcreteBundles({
                    variantID: nextBaseVariantID,
                    selectedUpgradeID: selectedUpgradeID,
                }),
            );
            dispatch(loadFinancingPlans({ productID: nextBaseVariantID }));
            // Track variant changes for pdp-hero configurator, see: #18585
            if (configType === "pdp-hero") {
                trackVariantSelectedEvent(nextBaseVariantID);
            }
        }

        // Update the page URL to include the new selection data
        if (
            enableHistoryInteraction &&
            (baseVariantChanged || upgradedVariantChanged)
        ) {
            pushPDPVariantURLChange(
                rootProduct,
                baseVariant,
                selectedUpgradeID,
            );
            // Refire Listrak AddProductBrowse activity only when the selected variant is changed.
            if (
                prevBaseVariantID.current &&
                baseVariant &&
                baseVariant.skus.length > 0
            ) {
                trackListrackProductBrowse(baseVariant.skus[0]);
            }
        }

        // Load the price data for quantities > 1
        if (
            nextUpgradedVariantID &&
            (upgradedVariantChanged || qtyChanged) &&
            quantity > 1
        ) {
            dispatch(loadPrice({ productID: nextUpgradedVariantID, quantity }));
        }

        // Update add to basket button text?
        if (upgradedVariantChanged && upgradedVariant) {
            updateAddToBasketText();
        }

        // Reload shipping methods if zip code changed
        if (prevPostCode.current !== postCode) {
            loadShippingMethods();
        }

        // Call Google remarketing analytics after products are loaded
        // prices update to 0 briefly during load, filter out a null price
        if (price && prevPrice.current !== price && configType === "pdp-hero") {
            trackPageView(upgradedVariant, rootProducts, price, baseVariant);
        }

        // If selectedUpgrade has changed, and upgrade is selectable in configurator, select it
        if (selectedUpgradeChanged && selectedUpgradeInConfigurator) {
            const product = rootProducts.filter(
                (p) => p.id === selectedUpgradeID,
            )[0];
            dispatch(setSelectedRootProduct(product));
        }
        // If selecting upsell changes rootproduct to match, clear selectedUpgrade
        if (
            rootProduct &&
            selectedUpgradeID &&
            rootProduct.id === selectedUpgradeID
        ) {
            dispatch(setSelectedUpgrade(null));
            pushPDPVariantURLChange(rootProduct, baseVariant, null);
        }

        // Update refs for next render
        prevBaseVariantID.current = nextBaseVariantID;
        prevUpgradedVariantID.current = nextUpgradedVariantID;
        prevQuantity.current = quantity;
        prevPostCode.current = postCode;
        prevPrice.current = price;
        prevSelectedUpgradeID.current = selectedUpgradeID;
    });

    return (
        <Component
            optionSet={optionSet}
            title={title}
            preheader={preheader}
            subhead={subhead}
            description={description}
            financingLink={financingLink}
            rootProduct={rootProduct}
            baseVariant={baseVariant}
            upgradedVariant={upgradedVariant}
            price={price}
            priceError={state.entities.priceError}
            financingPlans={financingPlans}
            concreteBundles={concreteBundles}
            showImage={showImage}
            promoCopy={promoCopy}
            chatLinkComponent={chatLinkComponent}
            liveChatHeader={liveChatHeader}
            basketLink={basketLink}
            configureGiftsLink={configureGiftsLink}
            showShippingLead={showShippingLead}
            selectedAddonPrice={selectedAddonPrice}
            showUpsellModal={showUpsellModal}
            showUpsellModalIcon={showUpsellModalIcon}
            getUpsellModalComponentFn={getUpsellModalComponentFn}
            buttonColor={buttonColor}
            configType={configType}
            getDeliveryCopy={getDeliveryCopy}
            showFeelScale={showFeelScale}
            showPreTitle={showPreTitle}
            learnMoreSelector={learnMoreSelector}
            hideLearnMoreLink={hideLearnMoreLink}
            showVariantCallout={showVariantCallout}
            starRatingURL={starRatingURL}
            strikeThroughMSRP={strikeThroughMSRP}
            actualPriceStyle={actualPriceStyle}
            boxHeaderContent={boxHeaderContent}
            topBadgeContent={topBadgeContent}
            overrideFinancingCopy={overrideFinancingCopy}
            supDollarSign={supDollarSign}
            showStarRatingsModal={showStarRatingsModal}
            showFinancingModal={showFinancingModal}
            phoneNumber={phoneNumber}
            optionSelectionErrorReason={optionSelectionErrorReason}
            showImagePerUpgradeOption={showImagePerUpgradeOption}
            isPlpPanelStyle={isPlpPanelStyle}
            useCompareDrawerCenter={useCompareDrawerCenter}
        >
            {buildAdditionalContentHelper(
                buildAdditionalContent,
                rootProduct,
                optionSet,
            )}
        </Component>
    );
};
